import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {MaybeCoreObject} from 'app-core/framework/CoreObjectConstants';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getModelManager, ManagedModelType} from 'models/ManagedModelDefinitions';
import {getStore} from 'redux-stores/Store';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';
import {EditableStudiesListPreviewStudiesQuery} from 'studies/studyable/__generated/EditableStudiesListPreviewStudiesQuery';
import {
  StudyableStudyAssociationsQuery,
  StudyableStudyAssociationsQuery_studyableItem_AaSequenceNode_studyAssociations_StudyAssociationNode_study_StudyNode as StudyNode,
} from 'studies/studyable/__generated/StudyableStudyAssociationsQuery';
import {StudyableStudyAssociationsQuery_studyableItem_AaSequenceNode_studyAssociations_StudyAssociationNode as StudyAssociationWithStudyOrInaccessibleNode} from 'studies/studyable/__generated/StudyableStudyAssociationsQuery';
import {isItemStudyable} from 'studies/studyable/StudyableUtils';
import {StudyAssociation, updateStudyables} from 'studies/StudyableDAO';
import StudySearchInput, {getStudyIds, useStudySearchControlledItemsProps} from 'studies/StudySearchInput';
import {getStudyItemListingSearchSessionId} from 'studies/workspace/StudySearchHelpers';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import {BLUE, BLUE_DARKER, GRAY_DARK, GRAY_LIGHTER, RED} from 'ui-library/Colors';
import useHoverState from 'ui-library/hooks/useHoverState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import APIUtil, {fileToOligoAwareFolderItemType} from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';

import RemoveAutoLinkingStudiesModal from './RemoveAutoLinkingStudiesModal';

type StudyAssociationWithStudyNode = Omit<StudyAssociationWithStudyOrInaccessibleNode, 'study'> & {
  study: StudyNode;
};

export async function updateStudyableStudyAssociations(
  studyableId: string,
  studyIds: Array<string>,
  prevStudyAssociations: Array<StudyAssociation & {name: string}>
): Promise<void> {
  const studyIdSet = new Set(studyIds);
  const prevAutoLinkingStudyAssociations = prevStudyAssociations
    .filter((association) => association.isAutoLinking)
    .map((association) => ({id: association.studyId, name: association.name}));
  const removedAutoLinkingStudyNames = prevAutoLinkingStudyAssociations
    .filter((study) => !studyIdSet.has(study.id))
    .map((study) => study.name);

  if (removedAutoLinkingStudyNames.length > 0) {
    const confirmCancel = await legacyRunModal(RemoveAutoLinkingStudiesModal, {
      studyNames: removedAutoLinkingStudyNames,
    });

    if (confirmCancel == null) {
      return;
    }
  }

  await updateStudyableStudies(studyableId, studyIds, prevStudyAssociations);
}

async function updateStudyableStudies(
  studyableId: string,
  studyIds: Array<string>,
  prevStudyAssociations: Array<StudyAssociation>
): Promise<void> {
  const autoLinkingStudyIds = new Set(
    prevStudyAssociations
      .filter((association) => association.isAutoLinking)
      .map((association) => association.studyId)
  );
  await updateStudyables({
    studyableAssociations: {
      [studyableId]: studyIds.map((studyId) => ({
        studyId,
        isAutoLinking: autoLinkingStudyIds.has(studyId),
      })),
    },
  });
}

function forceRefresh(studyIds: Array<string>): void {
  studyIds.forEach((studyId) => {
    const searchSessionId = getStudyItemListingSearchSessionId(studyId);
    getStore().dispatch(SearchUIReduxActions.forceRefreshSearch(searchSessionId));
  });
}

function EditingList({
  studyableId,
  exitEditing,
  studyAssociations,
  refetch,
  isEditingAutoLinkingStudies,
}: {
  studyableId: string;
  exitEditing: () => void;
  studyAssociations: Array<StudyAssociationWithStudyNode>;
  refetch: () => void;
  isEditingAutoLinkingStudies: boolean;
}): JSX.Element {
  const [isHoveringOnClose, closeHoverProps] = useHoverState();
  const [isHoveringOnSubmit, submitHoverProps] = useHoverState();
  const studies = studyAssociations.map((studyAssociation) => studyAssociation.study);
  const prevStudyAssociations = studyAssociations.map((studyAssociation) => ({
    studyId: studyAssociation.study.id,
    isAutoLinking: studyAssociation.isAutoLinking,
    name: studyAssociation.study.name,
  }));
  const studySearchProps = useStudySearchControlledItemsProps(studies);
  const selectedStudyIds = getStudyIds(studySearchProps);

  return (
    <div className={css(styles.wrapper)}>
      <StudySearchInput
        {...studySearchProps}
        className={css(styles.searchInput)}
        shouldAllowMultiple={true}
        chipSize={ChipSize.MINI}
      />
      <span className={css(styles.wrapper, styles.formIconWrapper)}>
        <BenchlingIcon
          className={css(
            styles.formIcon,
            isHoveringOnClose ? styles.hoveredCloseIcon : styles.unhoveredCloseIcon
          )}
          icon="close"
          {...closeHoverProps}
          onClick={() => {
            exitEditing();
          }}
        />
        <BenchlingIcon
          className={css(
            styles.formIcon,
            isHoveringOnSubmit ? styles.hoveredSubmitIcon : styles.unhoveredSubmitIcon
          )}
          icon="check"
          onClick={async () => {
            if (isEditingAutoLinkingStudies) {
              await updateStudyableStudyAssociations(studyableId, selectedStudyIds, prevStudyAssociations);
            } else {
              await updateStudyableStudies(studyableId, selectedStudyIds, prevStudyAssociations);
            }
            const allStudyIds = [...new Set([...selectedStudyIds, ...studies.map((study) => study.id)])];
            forceRefresh(allStudyIds);
            refetch();
            exitEditing();
          }}
          {...submitHoverProps}
        />
      </span>
    </div>
  );
}

export interface EditableStudiesListProps {
  studyableId: string;
  studyAssociations: Array<StudyAssociationWithStudyOrInaccessibleNode>;
  refetch: () => void;
  isWritable: boolean;
  isLoading: boolean;
  // If isEditingAutoLinkingStudies is true, we will show a confirmation modal before removing
  // any auto-linking studies. If false, we will just update the studyable with the new studies.
  isEditingAutoLinkingStudies?: boolean;
  maxChips?: number;
}

export type QueryingEditableStudiesListProps = {
  studyable: MaybeCoreObject;
  isWritable: boolean;
  renderWhileLoading?: () => JSX.Element;
  previewStudyIds?: Array<string>;
  isAutoLinkable?: boolean;
  maxChips?: number;
} & ({isWritable: true; previewStudyIds?: never} | {isWritable: false; previewStudyIds?: Array<string>});

export function EditableStudiesList({
  refetch,
  studyableId,
  studyAssociations,
  isWritable,
  isLoading,
  isEditingAutoLinkingStudies,
  maxChips,
}: EditableStudiesListProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, hoverProps] = useHoverState();

  // For now, if the item is in any Studies the user doesn't have access to, we don't let them edit the item's
  // Studies at all. A future improvement could allow editing the Studies, but would not allow removing the
  // inaccessible ones. See https://benchling.slack.com/archives/C03SF72HRRQ/p1693605936779319
  const allStudiesAreAccessible = studyAssociations.every(
    (studyAssociation): studyAssociation is StudyAssociationWithStudyNode =>
      !isInaccessible(studyAssociation.study)
  );

  const [tooltipTitle, disableEdit, onClickEditIcon] = useMemo(() => {
    if (isLoading) {
      return ['Loading...', true, undefined];
    } else if (!allStudiesAreAccessible) {
      return ['Cannot edit studies as some are inaccessible', true, undefined];
    } else {
      return [
        null,
        false,
        (): void => {
          setIsEditing(true);
        },
      ];
    }
  }, [isLoading, setIsEditing, allStudiesAreAccessible]);

  if (isEditing) {
    assert(
      allStudiesAreAccessible,
      'User must have permission to edit all studies of a studyable to edit any'
    );
    return (
      <EditingList
        studyAssociations={studyAssociations}
        studyableId={studyableId}
        exitEditing={() => {
          setIsEditing(false);
        }}
        refetch={refetch}
        isEditingAutoLinkingStudies={isEditingAutoLinkingStudies ?? false}
      />
    );
  }

  const autoLinkingStudyNames = isEditingAutoLinkingStudies
    ? studyAssociations
        .filter((studyAssociation) => studyAssociation.isAutoLinking)
        .map((studyAssociation) =>
          isInaccessible(studyAssociation.study) ? '(No access)' : studyAssociation.study.name
        )
        .join(', ')
    : [];

  const getStudyableModelType = (studyableId: string): ModelTypeConstants => {
    const studyableType = APIUtil.apiIdToType(studyableId);
    // If the studyable is a sequence, we need to further determine the type of sequence.
    if (studyableType === ModelTypeConstants.SEQUENCE) {
      const bioentitySummary = getModelManager(ManagedModelType.BIOENTITY_SUMMARY).getModel(studyableId);
      if (bioentitySummary) {
        return fileToOligoAwareFolderItemType(bioentitySummary);
      }
    }
    return studyableType;
  };

  return (
    <>
      <div
        className={css(
          styles.wrapper,
          isWritable ? styles.listWrapper : null,
          isHovering && isWritable ? styles.hover : null
        )}
        {...hoverProps}
        {...getTestProps({component: 'EditableStudiesList', element: 'root'})}
      >
        {studyAssociations.length > 0 ? (
          <ChipList
            itemIds={studyAssociations.map((studyAssociation) =>
              getAccessibleOrInaccessibleId(studyAssociation.study)
            )}
            isVertical={false}
            chipSize={ChipSize.MINI}
            maxChips={maxChips}
          />
        ) : (
          <div>This {typeToName(getStudyableModelType(studyableId))} has not been added to any studies.</div>
        )}
        {isHovering && isWritable && (
          <TooltipWrapper tooltipTitle={tooltipTitle}>
            <BenchlingIcon
              icon="edit"
              className={css(styles.editIcon, disableEdit && styles.editIconDisabled)}
              onClick={onClickEditIcon}
            />
          </TooltipWrapper>
        )}
      </div>
      {autoLinkingStudyNames.length > 0 && (
        <p className={css(styles.autoLinkingText)}>
          Items created in this entry will be automatically linked to <strong>{autoLinkingStudyNames}</strong>
          .
        </p>
      )}
    </>
  );
}

export const STUDYABLE_STUDY_ASSOCIATIONS_QUERY = gql`
  query StudyableStudyAssociationsQuery($studyableId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    studyableItem(id: $studyableId) {
      ...InaccessibleInfoGraphQL
      ... on StudyableInterface {
        id
        studyAssociations {
          isAutoLinking
          study {
            ... on StudyNode {
              id
              name
              humanId
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PREVIEW_STUDIES_QUERY = gql`
  query EditableStudiesListPreviewStudiesQuery($studyIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: STUDIES) {
    studies(ids: $studyIds) {
      ...InaccessibleInfoGraphQL
      ... on StudyNode {
        id
        name
        humanId
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Show all Studies a Studyable is in, and let the user add/remove Studies to/from that Studyable if they
 * have permission.
 *
 * Note: the UX and styling choices for the icons were made to match the Author field's behavior, since
 * they're next to each other in the UI. A followup could consolidate that logic somewhere shared.
 *
 * @param previewStudyIds - Use alongside `{isWritable: false}` to preview guided modification to studies,
 *   Note: the previewed studies are not rendered visually different from the existing studies, so the
 *   caller may want to draw attention to this.
 * @example
 * function FooEditor({foo, studyId}: {foo: Foo, studyId: string}) {
 *   return (
 *     <>
 *       <h1>Add foo to study</h1>
 *       <EditableStudiesList
 *         studyable={foo}
 *         isWritable={false}
 *         previewStudyIds={[studyId]}
 *       />
 *       <Button onClick={() => {
 *         FooDAO.update(foo.id, {appendStudyIds: [studyId]});
 *       }}>Update</Button>
 *     </>
 *   );
 * }
 */
function QueryingEditableStudiesList({
  studyable,
  isWritable,
  previewStudyIds,
  renderWhileLoading = () => <CenteredHorizontalThrobber />,
  isAutoLinkable,
  maxChips,
}: QueryingEditableStudiesListProps): JSX.Element | null {
  const shouldSkip = !BenchlingConfig.STUDIES_ENABLE || !isItemStudyable(studyable);

  const queryResult = useSkippableBenchlingQuery<StudyableStudyAssociationsQuery>(
    STUDYABLE_STUDY_ASSOCIATIONS_QUERY,
    {
      variables: {studyableId: studyable.id},
      renderCachedDataWhenRefetching: true,
      fetchPolicy: 'cache-and-network',
      skip: shouldSkip,
    }
  );

  const previewStudiesResult = useSkippableBenchlingQuery<EditableStudiesListPreviewStudiesQuery>(
    PREVIEW_STUDIES_QUERY,
    {
      variables: {studyIds: previewStudyIds},
      skip: shouldSkip || !previewStudyIds,
    }
  );

  if (queryResult.type === QueryResultType.SKIPPED) {
    return null;
  }

  if (queryResult.type === QueryResultType.PENDING || previewStudiesResult.type === QueryResultType.PENDING) {
    return renderWhileLoading();
  }

  if (queryResult.type === QueryResultType.ERROR || previewStudiesResult.type === QueryResultType.ERROR) {
    // todo
    return <div>error</div>;
  }

  if (isInaccessible(queryResult.data.studyableItem)) {
    return <div>You do not have permission to view this item.</div>;
  }

  const previewStudies =
    previewStudiesResult.type === QueryResultType.SUCCESS ? previewStudiesResult.data.studies : [];
  const existingStudyAssociations = queryResult.data.studyableItem.studyAssociations;
  const studyAssociations = existingStudyAssociations.concat(
    // Also include every study in previewStudyIds, ignoring any that are already in existing studies.
    // previewStudyIds are converted to StudyAssociationNodes with isAutoLinking set to false.
    // This prop is currently used by plates which cannot have auto-linking relationships with studies.
    // A follow up could be to change previewStudyIds to previewStudyAssociations which be an array of StudyAssociation objects.
    previewStudies
      .filter(
        (study) =>
          !existingStudyAssociations.some(
            (association) =>
              getAccessibleOrInaccessibleId(association.study) === getAccessibleOrInaccessibleId(study)
          )
      )
      .map((study) => ({
        __typename: 'StudyAssociationNode',
        study,
        isAutoLinking: false,
      }))
  );

  return (
    <EditableStudiesList
      studyableId={studyable.id}
      studyAssociations={studyAssociations}
      refetch={queryResult.refetch}
      isWritable={isWritable}
      isLoading={queryResult.loading}
      isEditingAutoLinkingStudies={isAutoLinkable}
      maxChips={maxChips}
    />
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  listWrapper: {
    padding: '3px',
  },
  formWrapper: {
    backgroundColor: GRAY_LIGHTER,
  },
  hover: {
    backgroundColor: GRAY_LIGHTER,
  },
  searchInput: {
    flexGrow: 1,
  },
  editIcon: {
    alignSelf: 'flex-end',
    color: GRAY_DARK,
    margin: 'auto 24px auto 0',
    cursor: 'pointer',
  },
  editIconDisabled: {
    cursor: 'not-allowed',
  },
  formIcon: {
    padding: '0 5px',
    cursor: 'pointer',
  },
  formIconWrapper: {
    marginRight: '19px',
  },
  unhoveredCloseIcon: {
    color: GRAY_DARK,
  },
  hoveredCloseIcon: {
    color: RED,
  },
  unhoveredSubmitIcon: {
    color: BLUE,
  },
  hoveredSubmitIcon: {
    color: BLUE_DARKER,
  },
  autoLinkingText: {
    ...FONT_PROXIMA_13,
  },
});

export default QueryingEditableStudiesList;
